import { useAnalytics } from '../../analytics/hooks/useAnalytics.js';
import { useWoody } from '../../woody/woody.js';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from '../../store/alerts.js';
import { AnalyticsEvents } from '../../analytics/analytics.types.js';
import { capitalizeFirstLetter } from '@fabric-app/utils/text';
import { rootQueryKeys } from '../../resource-roots/queries/rootQueryKeys.js';
import { userNavigationHistoryStore } from '../../app/stores/userNavigationHistoryStore.js';
import { useRouter } from '@fabric-app/compat';
import { getResourcePath } from '../utils/useRedirectFromShareToken.js';

const useMutationJoinViaShareToken = () => {
  const router = useRouter();
  const { client } = useWoody();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();
  const resetNavigationHistory = userNavigationHistoryStore((s) => s.resetHistory);
  const mutationJoin = useMutation({
    mutationFn: async ({ resourceId, accessToken }) => {
      const token = await client.v2({
        endpoint: "/v2/share-token/{accessToken}",
        params: {
          accessToken
        }
      });
      await client.v2({
        endpoint: "/v2/resources/{resourceId}/permissions/join",
        params: {
          resourceId
        }
      }, {
        method: "post",
        body: {
          accessToken
        }
      });
      return token;
    },
    onError: (error) => {
      if ("detail" in error) {
        if (error.detail === "err_cannot_join_owned") {
          return toast({
            content: "Can't join. You are the owner of this space"
          });
        }
      }
      return toast({
        content: "Failed to join"
      });
    },
    onSuccess: async (token, { resourceId, type, action }) => {
      track(AnalyticsEvents.SharingAcceptInvitation, {
        resourceId,
        type,
        action,
        role: token.role
      });
      toast({
        content: capitalizeFirstLetter(type === "resource" ? "item" : type) + " added to your library"
      });
      if (type === "space") {
        queryClient.invalidateQueries({
          queryKey: [
            rootQueryKeys.resourceRootList()[0]
          ]
        });
      }
      const redirectTo = getResourcePath({
        resourceId,
        type
      });
      if (redirectTo) {
        await router.push(redirectTo);
        resetNavigationHistory();
      }
    }
  });
  return mutationJoin;
};

export { useMutationJoinViaShareToken };
//# sourceMappingURL=useMutationJoinViaShareToken.js.map
